import { postWithProgress } from './upload.js';

/**
 * Resource functions for the admin (`/api/v1/admin/*`) and portal (`/api/v1/portal/*`) APIs.
 *
 * Conventions:
 * - List endpoints return the full Laravel paginator body `{ data, links, meta }` (or `{ data }`
 *   for unpaginated collections) – the caller needs `meta.total` for the DataTable.
 * - Detail / mutation endpoints return the unwrapped resource (`response.data`).
 * - Every function accepts `opts` (`{ signal }`) as last argument; TanStack Query passes its AbortSignal.
 *
 * @typedef {import('./client.js').ApiClient} ApiClient
 * @typedef {{ signal?: AbortSignal }} CallOptions
 *
 * @typedef {{
 *   page?: number,
 *   per_page?: number,
 *   sort?: string,
 *   filter?: Record<string, string|number|boolean|null|undefined>,
 * }} ListParams  spatie/laravel-query-builder style: `?filter[q]=…&sort=-name&page=2&per_page=25`
 *
 * @typedef {{ data: any[], links?: Record<string, string|null>, meta?: { current_page: number, last_page: number, per_page: number, total: number } }} Paginated
 */

/**
 * ListParams → query object for the client (`filter` becomes `filter[key]=value`).
 * Booleans are sent as 1/0 (Laravel's exact filter on boolean columns).
 * @param {ListParams} [params]
 */
export function toListQuery(params = {}) {
  const { page, per_page, sort, filter } = params;
  /** @type {Record<string, any>} */
  const query = {};
  if (page && page > 1) query.page = page;
  if (per_page) query.per_page = per_page;
  if (sort) query.sort = sort;
  if (filter) {
    const normalized = Object.fromEntries(
      Object.entries(filter)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, typeof v === 'boolean' ? (v ? 1 : 0) : v]),
    );
    if (Object.keys(normalized).length > 0) query.filter = normalized;
  }
  return query;
}

/** @param {any} res */
const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

/** @param {string|number} id */
const seg = (id) => encodeURIComponent(String(id));

/**
 * @typedef {{ from?: string, to?: string, property?: string|number }} PeriodParams  months as "YYYY-MM" (API default: last 12 months)
 * @typedef {{ op_type?: 'MIETE'|'BK'|'NEBENKOSTEN', property?: string|number }} OpenItemParams
 */

/** Drops empty values so that the query string stays minimal (and query keys stable). @param {Record<string, any>} [params] */
const compact = (params = {}) => Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));

/**
 * multipart/form-data body for uploads (Laravel conventions): arrays as `key[]`, booleans as 1/0,
 * empty values are left out. Files are passed through unchanged.
 * @param {Record<string, any>} fields
 */
export function toFormData(fields) {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null || value === '') continue;
    const append = (/** @type {string} */ name, /** @type {any} */ v) => form.append(name, typeof v === 'boolean' ? (v ? '1' : '0') : v);
    if (Array.isArray(value)) value.forEach((v) => append(`${key}[]`, v));
    else append(key, value);
  }
  return form;
}

/** `{ resend: true }` only when explicitly confirmed – otherwise no body at all. @param {{ resend?: boolean }} [options] */
const resendBody = (options = {}) => (options.resend ? { resend: true } : undefined);

/** @param {ApiClient} client */
export function createAdminApi(client) {
  /** @param {string} path @param {ListParams} [params] @param {CallOptions} [opts] @returns {Promise<Paginated>} */
  const list = (path, params, opts = {}) => client.get(path, { query: toListQuery(params), signal: opts.signal });
  /** @param {string} path @param {CallOptions} [opts] */
  const get = async (path, opts = {}) => unwrap(await client.get(path, { signal: opts.signal }));

  return {
    /** GET /admin/dashboard → `{ data, generated_at }` (keys only present with permission). */
    dashboard: (/** @type {CallOptions} */ opts = {}) => client.get('/admin/dashboard', { signal: opts.signal }),

    properties: {
      /** filters: q, portal_enabled, city, archived · sorts: name, number, city, postal_code, units_count */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/properties', params, opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/properties/${seg(id)}`, opts),
      /**
       * Only description, is_in_preparation, manager_party_id, contact_party_id, latitude, longitude
       * are writable – name/address come from WinCasa.
       * @param {string|number} id @param {Record<string, unknown>} payload
       */
      update: async (id, payload) => unwrap(await client.patch(`/admin/properties/${seg(id)}`, payload)),
      /** @param {string|number} id @param {{ current?: boolean }} [params] @param {CallOptions} [opts] */
      ownerships: async (id, params = {}, opts = {}) =>
        unwrap(await client.get(`/admin/properties/${seg(id)}/ownerships`, { query: { current: params.current ? 1 : undefined }, signal: opts.signal })),
      /** @param {string|number} id @param {{ status?: 'current'|'past' }} [params] @param {CallOptions} [opts] */
      tenancies: async (id, params = {}, opts = {}) =>
        unwrap(await client.get(`/admin/properties/${seg(id)}/tenancies`, { query: { status: params.status }, signal: opts.signal })),
      /**
       * Objektrollen (Kassenprüfer, Ableser …) → [{ id, role, source, party: { id, name }, valid_from, valid_to }];
       * `history` includes ended roles.
       * @param {string|number} id @param {{ history?: boolean }} [params] @param {CallOptions} [opts]
       */
      roles: async (id, params = {}, opts = {}) =>
        unwrap(await client.get(`/admin/properties/${seg(id)}/roles`, { query: { history: params.history ? 1 : undefined }, signal: opts.signal })),
      /** `properties.update`; 409 `role-exists`. @param {string|number} id @param {{ party_id: number, role: 'auditor'|'meter_reader' }} payload */
      assignRole: async (id, payload) =>
        unwrap(await client.post(`/admin/properties/${seg(id)}/roles`, { party_id: Number(payload.party_id), role: payload.role })),
      /** Ends the role (valid_to = yesterday, history stays) → `{ ended: true }`. @param {string|number} id @param {string|number} roleId */
      endRole: async (id, roleId) => unwrap(await client.delete(`/admin/properties/${seg(id)}/roles/${seg(roleId)}`)),
      /** `properties.archive` → `{ archived_at }`; 409 `property-has-active-units`. @param {string|number} id */
      archive: async (id) => unwrap(await client.post(`/admin/properties/${seg(id)}/archive`)),
      /** `properties.archive` → `{ archived_at: null }`. @param {string|number} id */
      restore: async (id) => unwrap(await client.post(`/admin/properties/${seg(id)}/restore`)),
      /**
       * Koordinaten über den konfigurierten Dienst ermitteln (F-115, `properties.update`) → PropertyResource.
       * 409 `geocoding-disabled`, 422 `geocoding-no-result` / `geocoding-incomplete-address`, 502 `geocoding-failed`.
       * @param {string|number} id
       */
      geocode: async (id) => unwrap(await client.post(`/admin/properties/${seg(id)}/geocode`)),
      /** G-04: `<img src>` of the property image (`properties.view`); `version` = `image_version` for cache busting. @param {string|number} id @param {string|number|null} [version] */
      imageUrl: (id, version) => client.url(`/admin/properties/${seg(id)}/image`, { v: version ?? undefined }),
      /** Upload/replace (JPEG/PNG/WebP/GIF, max. 10 MB, `properties.update`) → PropertyResource. @param {string|number} id @param {File|Blob} file */
      uploadImage: async (id, file) => unwrap(await client.post(`/admin/properties/${seg(id)}/image`, toFormData({ file }))),
      /** Removes the image (204). @param {string|number} id */
      removeImage: (id) => client.delete(`/admin/properties/${seg(id)}/image`),
    },

    units: {
      /** filters: property_id, type, q · sorts: number, designation, living_space */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/units', params, opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/units/${seg(id)}`, opts),
      /**
       * `units.update` – only `unit_type_id` and `notes` (master data comes from WinCasa) → UnitResource.
       * @param {string|number} id @param {{ unit_type_id?: number|null, notes?: string|null }} payload
       */
      update: async (id, payload) => unwrap(await client.patch(`/admin/units/${seg(id)}`, payload)),
      /** G-18: unit types for the edit select (`units.view`) → [{ id, key, label }]. @param {CallOptions} [opts] */
      types: (opts) => get('/admin/unit-types', opts),
    },

    /** Mietverhältnisse gesamt (read-only, source accounting). */
    tenancies: {
      /** filters: status (current|past|all, default current), property_id, search · 50 per page (max. 200) */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/tenancies', params, opts),
      /**
       * One tenancy (`tenancies.view`) → `{ data: TenancyResource (unit, parties), meta: { property: { id, name, number } } }`.
       * @param {string|number} id @param {CallOptions} [opts]
       */
      get: (id, opts = {}) => client.get(`/admin/tenancies/${seg(id)}`, { signal: opts.signal }),
      /**
       * Zahlungsverlauf (`finance.view`) → `{ available, basis, reason, tenancy, period, months: [{ period, due_on, due, paid, received,
       * outstanding, status: on_time|late|partial|missing|pending|no_due, paid_on, days_late }], summary, payments, rules, synced_at }`.
       * @param {string|number} id @param {{ months?: number }} [params] @param {CallOptions} [opts]
       */
      paymentHistory: async (id, params = {}, opts = {}) =>
        unwrap(await client.get(`/admin/tenancies/${seg(id)}/payment-history`, { query: compact(params), signal: opts.signal })),
      /** XLSX export with the list filters (`tenancies.export`). @param {ListParams['filter']} [filter] */
      exportUrl: (filter) => client.url('/admin/tenancies/export', toListQuery({ filter })),
    },

    parties: {
      /** filters: q, type, source, role (owner|tenant|client) · sorts: last_name, organization_name, created_at */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/parties', params, opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/parties/${seg(id)}`, opts),
      /** @param {Record<string, unknown>} payload */
      create: async (payload) => unwrap(await client.post('/admin/parties', payload)),
      /** `contacts` replaces only the manual contacts. @param {string|number} id @param {Record<string, unknown>} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/parties/${seg(id)}`, payload)),
      /** G-17: delete (`parties.delete`, 204); 409 `party-in-use` with `references: [{ key, label, count }]`. @param {string|number} id */
      remove: (id) => client.delete(`/admin/parties/${seg(id)}`),
      /** XLSX export with the list filters q, type, source, role (`parties.export`). @param {ListParams['filter']} [filter] */
      exportUrl: (filter) => client.url('/admin/parties/export', toListQuery({ filter })),
      /**
       * F-103: figures over all parties (`parties.view`) → `{ total, active, archived, by_type: { person, organization },
       * with_portal_account, by_role: { owner, tenant, client }, by_source: Record<string, number> }`.
       * @param {CallOptions} [opts]
       */
      statistics: (opts) => get('/admin/parties/statistics', opts),
      /**
       * Portalzugang für die Person anlegen (201, Benutzer) – `users.manage`. E-Mail optional (Standard: Haupt-E-Mail der Person),
       * Rollen Standard `['owner']`. 409 `party-has-user` / `email-taken`, 422 `email-missing`.
       * @param {string|number} id @param {{ email?: string|null, roles?: string[], send_invitation?: boolean }} [payload]
       */
      createAccess: async (id, payload = {}) => unwrap(await client.post(`/admin/parties/${seg(id)}/access`, payload)),
    },

    /**
     * Personen-Import aus CSV/XLSX (F-104, `parties.import`). Detail = full body `{ data, meta: { fields, encodings, delimiters, max_rows } }`;
     * data: `{ id, file_name, format, status, stats, encoding, delimiter, has_header, columns, mapping, sample: [{ row_number, cells }], previous_imports, import_run_id }`.
     */
    partyImports: {
      /** paginated, newest first */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/party-imports', params, opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts = {}) => client.get(`/admin/party-imports/${seg(id)}`, { signal: opts.signal }),
      /** CSV/XLSX upload (max. 10 MB) → full body (201). 422 `import-empty` / `import-too-many-rows`. @param {File|Blob} file @param {{ onProgress?: (f: number) => void, signal?: AbortSignal }} [opts] */
      upload: (file, opts = {}) => postWithProgress(client, '/admin/party-imports', toFormData({ file }), opts),
      /**
       * Apply the column mapping (`{ [columnIndex]: fieldKey|null }`) → preview (full body). 409 `import-locked`, 422 `import-mapping-incomplete`.
       * @param {string|number} id @param {{ mapping: Record<string, string|null>, has_header?: boolean, encoding?: string|null, delimiter?: string|null }} payload
       */
      applyMapping: (id, payload) => client.put(`/admin/party-imports/${seg(id)}/mapping`, payload),
      /** filters: status (error|warning|duplicate|create|update|skip|failed) · paginated rows with candidates. @param {string|number} id @param {ListParams} [params] @param {CallOptions} [opts] */
      rows: (id, params, opts) => list(`/admin/party-imports/${seg(id)}/rows`, params, opts),
      /** → `{ data: row, meta: { stats } }`. 422 `import-row-invalid` / `import-target-missing`. @param {string|number} id @param {string|number} rowId @param {{ decision: 'create'|'update'|'skip', target_party_id?: number|null }} payload */
      decide: (id, rowId, payload) => client.patch(`/admin/party-imports/${seg(id)}/rows/${seg(rowId)}`, payload),
      /** Queue the import run (202, full body). 409 `import-not-ready`. @param {string|number} id */
      execute: (id) => client.post(`/admin/party-imports/${seg(id)}/execute`),
      /** CSV result/error report. @param {string|number} id */
      reportUrl: (id) => client.url(`/admin/party-imports/${seg(id)}/report`),
      /** 204 – discards file and preview (created persons stay). @param {string|number} id */
      remove: (id) => client.delete(`/admin/party-imports/${seg(id)}`),
    },

    /**
     * Globale Suche (⌘K) → `{ properties?, units?, parties?, documents?, announcements? }` – each group only with the
     * matching permission, max. 8 hits `{ id, label, hint, link }`. `q` needs at least 2 characters (422 otherwise).
     * @param {string} q @param {CallOptions} [opts]
     */
    search: async (q, opts = {}) => unwrap(await client.get('/admin/search', { query: { q }, signal: opts.signal })),

    /** Slim pick lists without the page limit of the lists. */
    lookup: {
      /** → [{ id, name, number }] by name (default 500, max. 1000; archived only with `with_archived`). @param {{ q?: string, with_archived?: boolean, limit?: number }} [params] @param {CallOptions} [opts] */
      properties: async (params = {}, opts = {}) =>
        unwrap(
          await client.get('/admin/lookup/properties', {
            query: compact({ q: params.q, with_archived: params.with_archived ? 1 : undefined, limit: params.limit }),
            signal: opts.signal,
          }),
        ),
    },

    users: {
      /** filters: status, role, q · sorts: name, email, last_login_at, created_at */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/users', params, opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/users/${seg(id)}`, opts),
      /** G-09: permission catalog → [{ name, label, description, area, area_label, grantable }] (`users.view`). @param {CallOptions} [opts] */
      permissionCatalog: (opts) => get('/admin/permissions', opts),
      /** @param {{ name: string, email: string, roles: string[], locale?: string, access_expires_at?: string|null, send_invitation?: boolean, party_id?: number|null }} payload */
      create: async (payload) => unwrap(await client.post('/admin/users', payload)),
      /** @param {string|number} id @param {Record<string, unknown>} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/users/${seg(id)}`, payload)),
      /** 202 – sends the invitation (set-password link) again. @param {string|number} id */
      invite: (id) => client.post(`/admin/users/${seg(id)}/invite`),
      /** 204 @param {string|number} id */
      resetTwoFactor: (id) => client.post(`/admin/users/${seg(id)}/two-factor/reset`),
      /** Starts impersonation; returns the impersonated user's `/me`. @param {string|number} id */
      impersonate: async (id) => unwrap(await client.post(`/admin/users/${seg(id)}/impersonate`)),
    },

    /** GET /admin/roles → [{ name, permissions[] }] */
    roles: async (/** @type {CallOptions} */ opts = {}) => unwrap(await client.get('/admin/roles', { signal: opts.signal })),
    /**
     * Rollen × Rechte, read-only (`roles.manage`) → `{ areas: [{ key, label, permissions: [{ name, label, description }] }],
     * roles: [{ name, label, description, app, all_permissions, users_count, permissions[] }] }`.
     */
    roleMatrix: async (/** @type {CallOptions} */ opts = {}) => unwrap(await client.get('/admin/roles/matrix', { signal: opts.signal })),

    /** Änderungsprotokoll (`audit-log.view`); Werte sensibler Felder liefert die API nie. */
    auditLog: {
      /** filters: subject_type, subject_id, causer_id (id | 'system'), log_name, event, from, to (YYYY-MM-DD), q · newest first */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/audit-log', params, opts),
      /** → summary + `changes: [{ attribute, label, old, new }]`, `properties` @param {string|number} id @param {CallOptions} [opts] */
      get: (id, opts) => get(`/admin/audit-log/${seg(id)}`, opts),
      /** → `{ subject_types: [{ value, label }], log_names: [{ value, label }], events: string[], causers: [{ id, name }] }` */
      options: (/** @type {CallOptions} */ opts) => get('/admin/audit-log/options', opts),
    },

    /** Zentrale Kommentar-Moderation (`comments.moderate`); Aus-/Einblenden und Antworten über `documentChecks.*`. */
    comments: {
      /** filters: property_id, fiscal_year, document_check_id, status (active|hidden), kind (thread|reply), has_replies, author_id, q · `meta.authors` */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/comments', params, opts),
    },

    /** Dubletten-Prüfung (Migration §5): prüfen/verwerfen `parties.update`, zusammenführen zusätzlich `parties.delete`. */
    duplicates: {
      /** filters: status (open|resolved|all, default open), type, q · `meta.open` per type */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/duplicates', params, opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/duplicates/${seg(id)}`, opts),
      /** → `{ surviving_party_id, merged_party_id, references: [{ key, label, move, drop }], warnings[], blockers[] }` @param {string|number} id @param {string|number} partyId @param {CallOptions} [opts] */
      preview: async (id, partyId, opts = {}) =>
        unwrap(await client.get(`/admin/duplicates/${seg(id)}/merge-preview`, { query: { party_id: partyId }, signal: opts.signal })),
      /** „Kein Duplikat“; 409 `issue-already-resolved`. @param {string|number} id @param {string} [note] */
      dismiss: async (id, note) => unwrap(await client.post(`/admin/duplicates/${seg(id)}/dismiss`, compact({ note }))),
      /** 409 `issue-merged`. @param {string|number} id */
      reopen: async (id) => unwrap(await client.post(`/admin/duplicates/${seg(id)}/reopen`)),
      /** Kandidat bleibt, die Quell-Person geht in ihm auf; 409 `merge-blocked`/`source-party-missing`, 422 `invalid-merge-candidate`. @param {string|number} id @param {string|number} partyId @param {string} [note] */
      merge: async (id, partyId, note) => unwrap(await client.post(`/admin/duplicates/${seg(id)}/merge`, compact({ party_id: Number(partyId), note }))),
    },

    /** Finance reports over all (or one) properties – same shapes as the portal (`finance.view`). */
    finance: {
      /** → { properties: [{ property_id, name, accounts: [{ account_number, from_past_contract, items, total }], total, synced_at }], total } @param {OpenItemParams} [params] @param {CallOptions} [opts] */
      openItems: async (params, opts = {}) => unwrap(await client.get('/admin/finance/open-items', { query: compact(params), signal: opts.signal })),
      /** → { properties: [{ property_id, name, accounts: [{ account_number, name, months: [{ period, amount, cumulative }], total }], total }], total } @param {PeriodParams} [params] @param {CallOptions} [opts] */
      costs: async (params, opts = {}) => unwrap(await client.get('/admin/finance/costs', { query: compact(params), signal: opts.signal })),
      /** → { properties, months: [{ period, properties: [{ property_id, annual_rent, yield, deviation }], total_annual_rent, average_yield, total_deviation }], start_yield_average } @param {PeriodParams} [params] @param {CallOptions} [opts] */
      rentDevelopment: async (params, opts = {}) =>
        unwrap(await client.get('/admin/finance/rent-development', { query: compact(params), signal: opts.signal })),
      /**
       * G-19 / F-125: figures per property (max. 100 ids) → [{ property_id, units, open_items_balance, annual_rent, yield, yield_period, costs_last_6_months }].
       * @param {Array<string|number>} ids @param {CallOptions} [opts]
       */
      propertyFigures: async (ids, opts = {}) =>
        unwrap(await client.get('/admin/finance/property-figures', { query: { ids: ids.map(String) }, signal: opts.signal })),
    },

    importRuns: {
      /** filters: source, status · paginated, newest first */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/import-runs', params, opts),
      /** → [{ source, kind, last_success, stale }] (one entry per source/kind with at least one completed run) */
      health: (/** @type {CallOptions} */ opts) => get('/admin/import-runs/health', opts),
      /** F-316: targets of the manual sync (= `blimo:sync`, `integration.manage`) → [{ target, source, group, busy }] */
      syncTargets: (/** @type {CallOptions} */ opts) => get('/admin/import-runs/sync-targets', opts),
      /**
       * F-316: start a sync (`integration.manage`) → `{ target, source, status: 'queued' }` (202).
       * 409 `sync-already-requested` / `import-run-in-progress`.
       * @param {string} target
       */
      start: async (target) => unwrap(await client.post('/admin/import-runs', { target })),
    },

    /**
     * Belegprüfung (F-801…F-809). Detail answers `{ data, meta: { auditors: [{ party_id, name, confirmed }] } }` (full body);
     * mutations return the unwrapped DocumentCheckResource (without meta – refetch the detail).
     */
    documentChecks: {
      /** filters: property_id, fiscal_year · newest fiscal year first */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/document-checks', params, opts),
      /** → full body `{ data, meta }` */
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts = {}) => client.get(`/admin/document-checks/${seg(id)}`, { signal: opts.signal }),
      /** @param {{ property_id: number, fiscal_year: number, due_on?: string|null }} payload */
      create: async (payload) => unwrap(await client.post('/admin/document-checks', payload)),
      /** @param {string|number} id @param {{ due_on?: string|null, is_completed?: boolean }} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/document-checks/${seg(id)}`, payload)),
      /** 204; 409 `document-check-in-use` once released/confirmed/ready. @param {string|number} id */
      remove: (id) => client.delete(`/admin/document-checks/${seg(id)}`),
      /** @param {string|number} id @param {{ notify?: boolean }} [payload] notify the auditors (default true) */
      release: async (id, payload = {}) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/release`, payload)),
      withdraw: async (/** @type {string|number} */ id) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/withdraw`)),
      /** Mark as ready (all auditors confirmed) manually. @param {string|number} id */
      ready: async (id) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/ready`)),
      /** WinCasa booking list (CSV) → `{ rows, linked_receipts }`. @param {string|number} id @param {File|Blob} file */
      uploadBookingList: async (id, file) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/booking-list`, toFormData({ file }))),
      /**
       * Imported booking list → full body `{ data: { period_from, period_to, bank, header, rows: [{ row, cells, receipt_document_id }] }, meta: { current_page, last_page, total } }`;
       * 404 when nothing was imported. Server-side `search` over the cells.
       * @param {string|number} id @param {{ search?: string, page?: number, per_page?: number }} [params] @param {CallOptions} [opts]
       */
      bookingList: (id, params = {}, opts = {}) =>
        client.get(`/admin/document-checks/${seg(id)}/booking-list`, {
          query: compact({ search: params.search, page: params.page > 1 ? params.page : undefined, per_page: params.per_page }),
          signal: opts.signal,
        }),
      /** Receipt PDFs (max. 200) → `{ uploaded }`. @param {string|number} id @param {Array<File|Blob>} files */
      uploadReceipts: async (id, files) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/receipts`, toFormData({ files }))),
      /** → `{ recipients }`; 409 `document-check-not-released`. @param {string|number} id @param {string|number} documentId */
      notifyAuditors: async (id, documentId) =>
        unwrap(await client.post(`/admin/document-checks/${seg(id)}/notify-auditors`, { document_id: Number(documentId) })),
      /** → `{ recipients }` (document attached). @param {string|number} id @param {string|number} documentId */
      notifyOwners: async (id, documentId) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/notify-owners`, { document_id: Number(documentId) })),
      /** → `{ recipients }`; 409 `already-sent` unless `{ resend: true }`. @param {string|number} id @param {{ resend?: boolean }} [options] */
      ownerInfo: async (id, options) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/owner-info`, resendBody(options))),
      /** → `{ recipients }`; 409 `already-sent` unless `{ resend: true }`. @param {string|number} id @param {{ resend?: boolean }} [options] */
      ownerDocuments: async (id, options) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/owner-documents`, resendBody(options))),
      /** → `{ recipients }`; 409 `already-sent` unless `{ resend: true }` (`cost_statement_request_sent_at`). @param {string|number} id @param {{ resend?: boolean }} [options] */
      costStatementRequest: async (id, options) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/cost-statement-request`, resendBody(options))),
      /** Record an auditor's confirmation on their behalf; 422 `not-an-auditor`. @param {string|number} id @param {string|number} partyId */
      confirmOnBehalf: async (id, partyId) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/confirmations`, { party_id: Number(partyId) })),
      /** All comment threads incl. hidden ones. @param {string|number} id @param {CallOptions} [opts] */
      comments: (id, opts) => get(`/admin/document-checks/${seg(id)}/comments`, opts),
      /** @param {string|number} id @param {{ document_id: number, compare_document_id?: number|null, parent_id?: number|null, body: string }} payload */
      addComment: async (id, payload) => unwrap(await client.post(`/admin/document-checks/${seg(id)}/comments`, payload)),
      /** Moderation (`comments.moderate`). @param {string|number} id @param {string|number} commentId @param {boolean} isActive */
      moderateComment: async (id, commentId, isActive) =>
        unwrap(await client.patch(`/admin/document-checks/${seg(id)}/comments/${seg(commentId)}`, { is_active: isActive })),
    },

    /** Document folders (also the 7 folders of a Belegprüfung and announcement attachments). */
    documentFolders: {
      /** → DocumentResource[] (incl. `published_at`). @param {string|number} folderId @param {CallOptions} [opts] */
      documents: (folderId, opts) => get(`/admin/document-folders/${seg(folderId)}/documents`, opts),
      /**
       * Upload one file (`documents.upload`; `publish` needs `documents.publish`) → DocumentResource.
       * @param {string|number} folderId
       * @param {{ file: File|Blob, title?: string, description?: string, publish?: boolean, notify_owners?: boolean, attach?: boolean }} payload
       */
      upload: async (folderId, payload) => unwrap(await client.post(`/admin/document-folders/${seg(folderId)}/documents`, toFormData(payload))),
      /**
       * Folders of one owner scope (`documents.view`) → `[{ id, owner_type, owner_id, key, name, visible_in_portal, sort, documents_count }]`
       * ordered by `sort`, `name`. Always pass a scope – without it the API returns every folder (incl. Belegprüfung/Mitteilungen).
       * @param {{ owner_type: 'property'|'unit'|'global', owner_id?: string|number }} params @param {CallOptions} [opts]
       */
      list: async (params, opts = {}) => unwrap(await client.get('/admin/document-folders', { query: compact(params), signal: opts.signal })),
      /** (`documents.upload`) → FolderResource. @param {{ owner_type: 'property'|'unit'|'global', owner_id?: number|null, name: string, visible_in_portal?: boolean }} payload */
      create: async (payload) => unwrap(await client.post('/admin/document-folders', payload)),
      /** (`documents.upload`) → FolderResource. @param {string|number} id @param {{ name?: string, visible_in_portal?: boolean, sort?: number }} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/document-folders/${seg(id)}`, payload)),
      /**
       * Like `upload`, with progress (XMLHttpRequest) and the full body → `{ data: DocumentResource, meta: { notified } }`.
       * @param {string|number} folderId
       * @param {{ file: File|Blob, title?: string, description?: string, publish?: boolean, notify_owners?: boolean, attach?: boolean }} payload
       * @param {{ onProgress?: (fraction: number) => void, signal?: AbortSignal }} [opts]
       */
      uploadWithProgress: (folderId, payload, opts) =>
        postWithProgress(client, `/admin/document-folders/${seg(folderId)}/documents`, toFormData(payload), opts),
      /**
       * F-407: order of the documents in a folder (`documents.upload`) – all document ids of the folder exactly once
       * → DocumentResource[] in the new order; 422 `document-order-mismatch` if the list is stale.
       * @param {string|number} folderId @param {Array<string|number>} documentIds
       */
      reorder: async (folderId, documentIds) =>
        unwrap(await client.put(`/admin/document-folders/${seg(folderId)}/documents/order`, { document_ids: documentIds.map(Number) })),
      /** Title/description (`documents.upload`; 404 if not in this folder) → DocumentResource. @param {string|number} folderId @param {string|number} documentId @param {{ title?: string|null, description?: string|null }} payload */
      updateDocument: async (folderId, documentId, payload) =>
        unwrap(await client.patch(`/admin/document-folders/${seg(folderId)}/documents/${seg(documentId)}`, payload)),
      /**
       * Publish in this folder (`documents.publish`) → `{ notified }`. `notify_owners` only reaches owners for property folders
       * (not in preparation mode); `attach` sends the file along.
       * @param {string|number} folderId @param {string|number} documentId @param {{ notify_owners?: boolean, attach?: boolean }} [options]
       */
      publish: async (folderId, documentId, options = {}) => {
        const body = await client.post(`/admin/document-folders/${seg(folderId)}/documents/${seg(documentId)}/publish`, compact(options));
        return { notified: body?.meta?.notified ?? 0 };
      },
      /** (`documents.publish`) → 204. @param {string|number} folderId @param {string|number} documentId */
      unpublish: (folderId, documentId) => client.post(`/admin/document-folders/${seg(folderId)}/documents/${seg(documentId)}/unpublish`),
    },

    documents: {
      /** URL for `<a href>` / `<iframe src>` (`documents.view`). @param {string|number} id @param {{ inline?: boolean }} [options] */
      downloadUrl: (id, options = {}) => client.url(`/admin/documents/${seg(id)}/download`, { inline: options.inline ? 1 : undefined }),
      /** Soft delete (`documents.delete`) – removes the document from every folder it is linked to → 204. @param {string|number} id */
      remove: (id) => client.delete(`/admin/documents/${seg(id)}`),
    },

    /** Mitteilungen (F-501, F-502). Attachments via `documentFolders.upload(attachment_folder_id, …)`. */
    announcements: {
      /** filters: status (live|draft|scheduled|expired), property_id, search */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/announcements', params, opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/announcements/${seg(id)}`, opts),
      /** body_html is sanitized by the backend. @param {{ title: string, body_html?: string|null, is_published?: boolean, publish_at?: string|null, unpublish_at?: string|null, property_ids: number[] }} payload */
      create: async (payload) => unwrap(await client.post('/admin/announcements', payload)),
      /** @param {string|number} id @param {Record<string, unknown>} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/announcements/${seg(id)}`, payload)),
      /** 204 @param {string|number} id */
      remove: (id) => client.delete(`/admin/announcements/${seg(id)}`),
      /** → `{ count, portal_users: [{ id, name, email }], email_only: string[] }` (`announcements.send`). */
      recipients: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/announcements/${seg(id)}/recipients`, opts),
      /**
       * Queue the dispatch → `{ dispatch_id, status }`. 409 `already-dispatched` (repeat only with `resend: true`),
       * `dispatch-in-progress`, `announcement-not-published`.
       * @param {string|number} id @param {{ with_attachments?: boolean, resend?: boolean }} [payload]
       */
      /** F-501: `<img src>` of the image (`announcements.view`); `version` = `image_version`. @param {string|number} id @param {string|number|null} [version] */
      imageUrl: (id, version) => client.url(`/admin/announcements/${seg(id)}/image`, { v: version ?? undefined }),
      /** Upload/replace (JPEG/PNG/WebP/GIF, max. 10 MB, `announcements.manage`) → AnnouncementResource. @param {string|number} id @param {File|Blob} file */
      uploadImage: async (id, file) => unwrap(await client.post(`/admin/announcements/${seg(id)}/image`, toFormData({ file }))),
      /** Removes the image (204). @param {string|number} id */
      removeImage: (id) => client.delete(`/admin/announcements/${seg(id)}/image`),
      dispatch: async (id, payload = {}) => {
        const body = {
          ...(payload.with_attachments !== undefined ? { with_attachments: payload.with_attachments } : {}),
          ...(payload.resend ? { resend: true } : {}),
        };
        return unwrap(await client.post(`/admin/announcements/${seg(id)}/dispatch`, body));
      },
    },

    /** Posteingang (owner messages). The list body carries `meta.open` (open threads overall). */
    messages: {
      /** filters: status (open|in_progress|closed), property_id, search, assigned_to_me */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/messages', params, opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/messages/${seg(id)}`, opts),
      /** `messages.reply` → thread incl. messages. @param {string|number} id @param {{ body: string, files?: Array<File|Blob> }} payload */
      reply: async (id, payload) => unwrap(await client.post(`/admin/messages/${seg(id)}/replies`, toFormData(payload))),
      /** 422 `invalid-assignee`. @param {string|number} id @param {{ status?: string, assigned_to?: number|null }} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/messages/${seg(id)}`, payload)),
      /** Assignable users (active, `messages.reply`) → [{ id, name, email }]. */
      assignees: (/** @type {CallOptions} */ opts) => get('/admin/messages/assignees', opts),
    },

    /** Versandhistorie (F-507). */
    outboundMessages: {
      /** filters: search, source (platform|oldportal), status (sent|failed), from, to (YYYY-MM-DD) · default 50 per page */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/outbound-messages', params, opts),
      /** → summary + `body_html` (null when redacted) + `error` */
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/outbound-messages/${seg(id)}`, opts),
      /** 202 → `{ resent: true }`; 409 `not-resendable`. @param {string|number} id */
      resend: async (id) => unwrap(await client.post(`/admin/outbound-messages/${seg(id)}/resend`)),
    },

    /** Mail-Vorlagen (F-506). */
    mailTemplates: {
      /** → [{ key, label, customized, updated_at }] */
      list: (/** @type {CallOptions} */ opts) => get('/admin/mail-templates', opts),
      /** → { key, label, placeholders, legacy_key, customized, subject, body_html, default_subject, default_body_html } */
      get: (/** @type {string} */ key, /** @type {CallOptions} */ opts) => get(`/admin/mail-templates/${seg(key)}`, opts),
      /** 422 `unknown-placeholder` with `placeholders: string[]`. @param {string} key @param {{ subject: string, body_html: string }} payload */
      update: async (key, payload) => unwrap(await client.put(`/admin/mail-templates/${seg(key)}`, payload)),
      /** Drop the custom text (default applies again) – 204. @param {string} key */
      reset: (key) => client.delete(`/admin/mail-templates/${seg(key)}`),
      /**
       * Renders the template with sample values `[name]` → `{ subject, body_html, customized }`. Without `draft` the STORED
       * template is rendered; with `draft` (`subject`/`body_html` of the unsaved form) the draft is rendered instead.
       * @param {string} key @param {Record<string, string>} [values] @param {{ subject?: string, body_html?: string }} [draft]
       */
      preview: async (key, values, draft) => {
        const body = { ...(values ? { values } : {}), ...(draft ? compact({ subject: draft.subject, body_html: draft.body_html }) : {}) };
        return unwrap(await client.post(`/admin/mail-templates/${seg(key)}/preview`, Object.keys(body).length > 0 ? body : undefined));
      },
    },

    /** Zähler & Versorger (F-901, F-902). DELETE archives (open readings are removed, history stays). */
    meters: {
      /** filters: property_id, supplier_id, reader_party_id, archived, search · default 50 per page */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/meters', params, opts),
      /** → MeterResource incl. `readings` (history, newest first is not guaranteed). */
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/meters/${seg(id)}`, opts),
      /** @param {{ property_id: number, number: string, type?: string|null, contract_number?: string|null, supplier_id?: number|null, reader_party_id?: number|null, description?: string|null }} payload */
      create: async (payload) => unwrap(await client.post('/admin/meters', payload)),
      /** @param {string|number} id @param {Record<string, unknown>} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/meters/${seg(id)}`, payload)),
      /** Archive (soft delete) → MeterResource with `archived_at`. @param {string|number} id */
      archive: async (id) => unwrap(await client.delete(`/admin/meters/${seg(id)}`)),
    },

    meterSuppliers: {
      /** → [{ id, name, cobra_address_id, meters_count }] (active meters) */
      list: (/** @type {CallOptions} */ opts) => get('/admin/meter-suppliers', opts),
      /** @param {{ name: string }} payload */
      create: async (payload) => unwrap(await client.post('/admin/meter-suppliers', payload)),
      /** @param {string|number} id @param {{ name: string }} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/meter-suppliers/${seg(id)}`, payload)),
    },

    /** Zählerstände eines Jahres (F-903…F-906). */
    meterReadings: {
      /**
       * → full body `{ data, meta: { …paginator, summary: { year, open, read, closed, total } } }`, rows incl. `previous_value`.
       * filters: status (open|read|closed), property_id, supplier_id, reader_party_id, search · default 100 per page (max. 500)
       * @param {number|string} year @param {ListParams} [params] @param {CallOptions} [opts]
       */
      list: (year, params = {}, opts = {}) => client.get('/admin/meter-readings', { query: { year, ...toListQuery(params) }, signal: opts.signal }),
      /** Inline entry/correction; `value: null` sets the reading back to open. @param {string|number} id @param {{ value: number|null, read_at?: string|null, is_new_meter?: boolean, manual?: boolean }} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/meter-readings/${seg(id)}`, payload)),
      /**
       * FI-903a: single reading for one meter and any year (`meter-readings.manage`) → MeterReadingResource (201);
       * without `value` an open reading is created. 409 `meter-reading-exists`, `meter-archived`.
       * @param {string|number} meterId @param {{ year: number, value?: number|null, read_at?: string|null, is_new_meter?: boolean }} payload
       */
      create: async (meterId, payload) => unwrap(await client.post(`/admin/meters/${seg(meterId)}/readings`, payload)),
      /** Photo 1 or 2 of a reading (inline). @param {string|number} id @param {1|2} slot */
      photoUrl: (id, slot) => client.url(`/admin/meter-readings/${seg(id)}/photos/${slot === 2 ? 2 : 1}`),
      /** XLSX export (`meters.export`). @param {{ year: number|string, supplier_id?: string|number, reader_party_id?: string|number, kind?: 'readings'|'changes' }} params */
      exportUrl: (params) => client.url('/admin/meter-readings/export', compact(params)),
    },

    /** Ablesejahre und Ableser (F-904, F-905, F-907, F-909). */
    meterYears: {
      /** → { year, open, read, closed, total } @param {number|string} year @param {CallOptions} [opts] */
      get: (year, opts) => get(`/admin/meter-years/${seg(year)}`, opts),
      /** Creates the open readings for all active meters → { created, …summary }. @param {number} year */
      open: async (year) => unwrap(await client.post('/admin/meter-years', { year: Number(year) })),
      /** 409 `stale-confirmation` when `expectedOpen` differs from the current number of open readings. @param {number|string} year @param {number} expectedOpen */
      close: async (year, expectedOpen) => unwrap(await client.post(`/admin/meter-years/${seg(year)}/close`, { expected_open: Number(expectedOpen) })),
      /** App (false) or manual (true) for all readings of a reader in the year → { updated }. @param {number|string} year @param {string|number} partyId @param {boolean} manual */
      readerMode: async (year, partyId, manual) =>
        unwrap(await client.post(`/admin/meter-years/${seg(year)}/readers/${seg(partyId)}/mode`, { manual: Boolean(manual) })),
      /**
       * Series mail to all readers with open meters → full body `{ data: [{ party_id, name, email, open, sent }], meta: { dry_run, missing_email } }`.
       * @param {number|string} year @param {{ kind: 'first'|'reminder', dry_run?: boolean }} payload
       */
      readerMails: (year, payload) => client.post(`/admin/meter-years/${seg(year)}/reader-mails`, { kind: payload.kind, dry_run: Boolean(payload.dry_run) }),
      /** Prefilled single mail → { subject, body_html, to }. @param {number|string} year @param {string|number} partyId @param {CallOptions} [opts] */
      readerMailDraft: (year, partyId, opts) => get(`/admin/meter-years/${seg(year)}/readers/${seg(partyId)}/mail`, opts),
      /**
       * Sends the single mail (recipient fixed by the API) → { sent_to }; 422 `reader-without-email`.
       * @param {number|string} year @param {string|number} partyId @param {{ subject: string, body_html: string, files?: Array<File|Blob> }} payload
       */
      sendReaderMail: async (year, partyId, payload) =>
        unwrap(await client.post(`/admin/meter-years/${seg(year)}/readers/${seg(partyId)}/mail`, toFormData(payload))),
    },

    /** Stimmrechtsvollmachten (read-only overview). */
    proxyAuthorizations: {
      /** filters: property_id, from, to (YYYY-MM-DD), confirmed · newest meeting first, 50 per page */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/proxy-authorizations', params, opts),
      /** PDF (only for confirmed authorizations, 409 `not-confirmed` otherwise). @param {string|number} id */
      pdfUrl: (id) => client.url(`/admin/proxy-authorizations/${seg(id)}/pdf`),
    },

    /** Vermietungs-Tickets (read-only, source Cobra). */
    lettingTickets: {
      /** filters: property_id, open, status → `{ data, meta: { current_page, last_page, total } }` */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/letting-tickets', params, opts),
    },

    /**
     * Mandanten & Freigaben (F-013). Read: `mandates.manage` or `finance.view`; changes: `mandates.manage`.
     * Bulk operations are idempotent and answer with the ids/numbers that actually changed.
     */
    mandates: {
      /** filters: q, archived → `{ data: [{ id, name, source, archived_at, flagged, counts: { units, properties, members, cost_accounts } }], meta }` */
      list: (/** @type {ListParams} */ params, /** @type {CallOptions} */ opts) => list('/admin/mandates/clients', params, opts),
      /** → { …summary, notes, reports, properties: [{ id, name, number, units: [...], cost_accounts: [{ number, name }] }], members: [...] } */
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/admin/mandates/clients/${seg(id)}`, opts),
      /** New organisation (`organization_name`) or mark an existing one (`party_id`) → detail (201 new, 200 already a client). @param {{ organization_name?: string, party_id?: number }} payload */
      create: async (payload) => unwrap(await client.post('/admin/mandates/clients', payload)),
      /** Removes the client flag (409 `client-in-use` while units/accounts/members exist). @param {string|number} id */
      remove: (id) => client.delete(`/admin/mandates/clients/${seg(id)}`),
      /** → [{ id, name, number, archived, units_count, assigned_units_count, released_accounts_count }] @param {string|number} id @param {{ q?: string }} [params] @param {CallOptions} [opts] */
      properties: async (id, params = {}, opts = {}) =>
        unwrap(await client.get(`/admin/mandates/clients/${seg(id)}/properties`, { query: toListQuery({ filter: { q: params.q } }), signal: opts.signal })),
      /**
       * → { property, units: [{ id, number, designation, type, archived, assigned, other_clients }], cost_accounts: [{ number, name, in_ledger, has_bookings, released }] }
       * @param {string|number} id @param {string|number} propertyId @param {CallOptions} [opts]
       */
      property: (id, propertyId, opts) => get(`/admin/mandates/clients/${seg(id)}/properties/${seg(propertyId)}`, opts),
      /** Sets the assigned units of one property → `{ added, removed, property }`. @param {string|number} id @param {string|number} propertyId @param {number[]} unitIds */
      syncUnits: async (id, propertyId, unitIds, options = {}) =>
        unwrap(
          await client.put(`/admin/mandates/clients/${seg(id)}/properties/${seg(propertyId)}/units`, {
            unit_ids: unitIds.map(Number),
            ...(options.move ? { move: true } : {}),
          }),
        ),
      /** Sets the released cost accounts of one property → `{ added, removed, property }`. @param {string|number} id @param {string|number} propertyId @param {string[]} accountNumbers */
      syncCostAccounts: async (id, propertyId, accountNumbers) =>
        unwrap(
          await client.put(`/admin/mandates/clients/${seg(id)}/properties/${seg(propertyId)}/cost-accounts`, { account_numbers: accountNumbers.map(String) }),
        ),
      /** Bulk assign → `{ added }`. @param {string|number} id @param {{ unit_ids?: number[], property_ids?: number[] }} selection */
      assignUnits: async (id, selection) => unwrap(await client.post(`/admin/mandates/clients/${seg(id)}/units`, selection)),
      /** Bulk unassign → `{ removed }`. @param {string|number} id @param {{ unit_ids?: number[], property_ids?: number[] }} selection */
      unassignUnits: async (id, selection) => unwrap(await client.delete(`/admin/mandates/clients/${seg(id)}/units`, { body: selection })),
      /** → `{ added }` @param {string|number} id @param {{ property_id: number, account_numbers: string[] }} payload */
      releaseCostAccounts: async (id, payload) => unwrap(await client.post(`/admin/mandates/clients/${seg(id)}/cost-accounts`, payload)),
      /** → `{ removed }` @param {string|number} id @param {{ property_id: number, account_numbers: string[] }} payload */
      revokeCostAccounts: async (id, payload) => unwrap(await client.delete(`/admin/mandates/clients/${seg(id)}/cost-accounts`, { body: payload })),
      /** Adds a person (member_of_client) → member (201 new, 200 already member). @param {string|number} id @param {number|string} partyId */
      addMember: async (id, partyId) => unwrap(await client.post(`/admin/mandates/clients/${seg(id)}/members`, { party_id: Number(partyId) })),
      /** 204 @param {string|number} id @param {string|number} partyId */
      removeMember: (id, partyId) => client.delete(`/admin/mandates/clients/${seg(id)}/members/${seg(partyId)}`),
      /** Monatsberichte (F-018, `mandates.manage`) → flags. @param {string|number} id @param {{ send_open_items_list?: boolean, send_costs_summary?: boolean, send_monthly_yield?: boolean }} flags */
      updateReports: async (id, flags) => unwrap(await client.patch(`/admin/mandates/clients/${seg(id)}/reports`, flags)),
      /**
       * → `{ period, sections, recipients: [{ user_id, name, email, eligible, reason, dispatch }], mail: { subject, body_html, attachment, facts }|null }`.
       * @param {string|number} id @param {{ month?: string, user_id?: number }} [params] @param {CallOptions} [opts]
       */
      reportPreview: async (id, params = {}, opts = {}) =>
        unwrap(await client.get(`/admin/mandates/clients/${seg(id)}/reports/preview`, { query: compact(params), signal: opts.signal })),
      /** Sends now (idempotent per recipient + month) → `{ period, results: [{ user_id, status, reason }] }`. @param {string|number} id @param {{ month?: string }} [payload] */
      sendReport: async (id, payload = {}) => unwrap(await client.post(`/admin/mandates/clients/${seg(id)}/reports/send`, payload)),
    },

    /** Nützliche Links (F-1003). */
    links: {
      /** → full body `{ data: [{ id, name, position, links: [...] }], meta: { uncategorized: [...] } }` */
      list: (/** @type {CallOptions} */ opts = {}) => client.get('/admin/links', { signal: opts.signal }),
      /** @param {{ name: string, position?: number }} payload */
      createCategory: async (payload) => unwrap(await client.post('/admin/link-categories', payload)),
      /** @param {string|number} id @param {{ name?: string, position?: number }} payload */
      updateCategory: async (id, payload) => unwrap(await client.patch(`/admin/link-categories/${seg(id)}`, payload)),
      /** 204 – the links stay (without category). @param {string|number} id */
      removeCategory: (id) => client.delete(`/admin/link-categories/${seg(id)}`),
      /**
       * F-1005: `audience` all|roles|users with `roles[]` resp. `user_ids[]`; `is_highlighted` = button/card on the portal home.
       * Responses include `roles` and `users: [{ id, name, email }]`.
       * @param {{ name: string, url: string, description?: string|null, link_category_id?: number|null, position?: number, is_active?: boolean, is_highlighted?: boolean, audience?: 'all'|'roles'|'users', roles?: string[], user_ids?: number[] }} payload
       */
      create: async (payload) => unwrap(await client.post('/admin/links', payload)),
      /** @param {string|number} id @param {Record<string, unknown>} payload */
      update: async (id, payload) => unwrap(await client.patch(`/admin/links/${seg(id)}`, payload)),
      /** 204 @param {string|number} id */
      remove: (id) => client.delete(`/admin/links/${seg(id)}`),
    },

    /** Einstellungen / Branding (F-014/F-015, `settings.manage`). */
    settings: {
      /** → full body `{ data: { branding, email, mailboxes, system }, meta: { defaults, contrast, warnings, limits } }` (values null = default). */
      get: (/** @type {CallOptions} */ opts = {}) => client.get('/admin/settings', { signal: opts.signal }),
      /**
       * Partial update by group; null/'' restores the default. → same body as `get` (incl. `meta.warnings`).
       * @param {{ branding?: Record<string, unknown>, email?: Record<string, unknown>, mailboxes?: Record<string, unknown> }} payload
       */
      update: (payload) => client.put('/admin/settings', payload),
      /**
       * PNG/WebP/JPEG/SVG (SVG with scripts → 422) → `{ url, mime, size, width, height, original_name, uploaded_at }`.
       * @param {'logo-light'|'logo-dark'|'favicon'} asset @param {File|Blob} file @param {{ onProgress?: (f: number) => void, signal?: AbortSignal }} [opts]
       */
      uploadAsset: async (asset, file, opts = {}) =>
        unwrap(await postWithProgress(client, `/admin/settings/branding/${seg(asset)}`, toFormData({ file }), opts)),
      /** @param {'logo-light'|'logo-dark'|'favicon'} asset */
      removeAsset: (asset) => client.delete(`/admin/settings/branding/${seg(asset)}`),
    },
  };
}

/**
 * Public endpoints (no session needed) – used before login.
 * @param {ApiClient} client
 */
export function createPublicApi(client) {
  return {
    /** GET /branding → `{ company_name, short_name, primary_color, primary_foreground, welcome_text, imprint_url, privacy_url, support_phone, support_email, logo_light_url, logo_dark_url, favicon_url, customized }` */
    branding: async (/** @type {CallOptions} */ opts = {}) => unwrap(await client.get('/branding', { signal: opts.signal })),
  };
}

/** @param {ApiClient} client */
export function createPortalApi(client) {
  /** @param {string} path @param {CallOptions} [opts] */
  const get = async (path, opts = {}) => unwrap(await client.get(path, { signal: opts.signal }));

  return {
    properties: {
      /** All properties visible to the user (unpaginated). */
      list: (/** @type {CallOptions} */ opts) => get('/portal/properties', opts),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/properties/${seg(id)}`, opts),
      /** Owner list of the WEG – 404 unless the user currently owns a unit there. */
      owners: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/properties/${seg(id)}/owners`, opts),
      /** → { available, reason?: 'missing_baseline', years: [{ year, income, costs, cumulative_income, cumulative_costs, yield }] } */
      accumulatedDevelopment: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) =>
        get(`/portal/properties/${seg(id)}/accumulated-development`, opts),
      /** → current baseline `{ purchase_price, initial_rent, initial_rental_income, initial_costs, purchase_month, updated_at }` or null */
      investmentBaseline: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/properties/${seg(id)}/investment-baseline`, opts),
      /**
       * Stores a new version (`finance.baselines.manage`). purchase_month: "YYYY-MM" or null.
       * @param {string|number} id
       * @param {{ purchase_price: number, initial_rent: number, initial_rental_income: number, initial_costs: number, purchase_month?: string|null }} payload
       */
      updateInvestmentBaseline: async (id, payload) => unwrap(await client.put(`/portal/properties/${seg(id)}/investment-baseline`, payload)),
      /** G-04: `<img src>` of the property image (404 if none/not visible). @param {string|number} id @param {string|number|null} [version] */
      imageUrl: (id, version) => client.url(`/portal/properties/${seg(id)}/image`, { v: version ?? undefined }),
    },
    units: {
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/units/${seg(id)}`, opts),
      /**
       * Mietinformationen → `{ unit, tenancy: { account_number, contract_signed_on, starts_on, ends_on, terminated_on, vat_reporting, tenants: [{ role, name }]|null }|null, rent }`.
       * 404 without access (tenancies.view or own unit with lettings.view); `tenants` is null for owners (data minimisation).
       * @param {string|number} id @param {CallOptions} [opts]
       */
      tenancy: (id, opts) => get(`/portal/units/${seg(id)}/tenancy`, opts),
      /**
       * Zahlungsverlauf des aktuellen Mietverhältnisses (12 Monate) → like admin `tenancies.paymentHistory` (without tenancy id) or null.
       * 404 without access (finance.view within the finance scope, or own unit with lettings.view – then without booking texts).
       * @param {string|number} id @param {CallOptions} [opts]
       */
      paymentHistory: (id, opts) => get(`/portal/units/${seg(id)}/payment-history`, opts),
    },

    /** Startseite → `{ tiles: { unread_announcements?, unread_message_replies?, open_document_checks?, open_meter_readings?, upcoming_proxies? }, contacts: [{ role, name, email, phone }], properties }` – tiles only with permission. */
    home: (/** @type {CallOptions} */ opts) => get('/portal/home', opts),

    /** Profil (F-008/F-016): master data read-only; password via `auth.updatePassword` (Fortify). */
    profile: {
      /** → { name, email, party: { name, letter_salutation, contacts: [{ kind, value }], address }|null, consents: { share_contact_with_co_owners }, two_factor_enabled } */
      get: (/** @type {CallOptions} */ opts) => get('/portal/profile', opts),
      /** → updated profile; 404 without a linked person. @param {{ share_contact_with_co_owners: boolean }} payload */
      updateConsents: async (payload) => unwrap(await client.patch('/portal/profile/consents', payload)),
    },

    /** Bewohnerliste (investors/clients, `tenancies.view`) – only visible units. */
    tenancies: {
      /** filters: status (current|past|all, default current), property_id, search → paginator `{ data, meta }`. @param {ListParams} [params] @param {CallOptions} [opts] */
      list: (params = {}, opts = {}) => client.get('/portal/tenancies', { query: toListQuery(params), signal: opts.signal }),
      /** XLSX export with the list filters (`tenancies.export`). @param {ListParams['filter']} [filter] */
      exportUrl: (filter) => client.url('/portal/tenancies/export', toListQuery({ filter })),
    },

    /** Investor finance views (`finance.view`; 403 otherwise). Scope = units visible to the user. */
    finance: {
      /** → { properties, units, rented_units, average_yield, average_yield_period, open_items_total, costs_last_6_months } */
      summary: (/** @type {CallOptions} */ opts) => get('/portal/finance/summary', opts),
      /** Months without income are omitted by the API. @param {PeriodParams} [params] @param {CallOptions} [opts] */
      rentDevelopment: async (params, opts = {}) =>
        unwrap(await client.get('/portal/finance/rent-development', { query: compact(params), signal: opts.signal })),
      /** @param {PeriodParams} [params] @param {CallOptions} [opts] */
      costs: async (params, opts = {}) => unwrap(await client.get('/portal/finance/costs', { query: compact(params), signal: opts.signal })),
      /** Amounts in display sign (negative = arrears). @param {OpenItemParams} [params] @param {CallOptions} [opts] */
      openItems: async (params, opts = {}) => unwrap(await client.get('/portal/finance/open-items', { query: compact(params), signal: opts.signal })),
      /** G-19 / F-125: figures per visible property → [{ property_id, units, open_items_balance, annual_rent, yield, yield_period, costs_last_6_months|null }]. */
      properties: (/** @type {CallOptions} */ opts) => get('/portal/finance/properties', opts),
    },

    /**
     * Belegprüfung im Portal: auditors (released checks of their properties) and owners (completed checks,
     * last 5 years). Folder visibility is decided by the API.
     */
    documentChecks: {
      /** → [{ id, property, fiscal_year, due_on, is_ready, is_completed, i_am_auditor, confirmed_by_me }] @param {{ property?: string|number }} [params] @param {CallOptions} [opts] */
      list: async (params = {}, opts = {}) => unwrap(await client.get('/portal/document-checks', { query: compact(params), signal: opts.signal })),
      /** → { id, property, fiscal_year, due_on, is_ready, i_am_auditor, confirmed_by_me, has_booking_list, folders: [{ id, key, name, documents }] } */
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/document-checks/${seg(id)}`, opts),
      /** Auditor confirms → `{ confirmed, is_ready }` (404 for non-auditors). @param {string|number} id */
      confirm: async (id) => unwrap(await client.post(`/portal/document-checks/${seg(id)}/confirm`)),
      /**
       * WinCasa booking list – auditors only (404 otherwise) → full body `{ data: { period_from, period_to, bank, header, rows: [{ row, cells, receipt_document_id }] }, meta: { current_page, last_page, total } }`.
       * @param {string|number} id @param {{ page?: number, per_page?: number }} [params] @param {CallOptions} [opts]
       */
      bookingList: (id, params = {}, opts = {}) =>
        client.get(`/portal/document-checks/${seg(id)}/booking-list`, {
          query: compact({ page: params.page > 1 ? params.page : undefined, per_page: params.per_page }),
          signal: opts.signal,
        }),
      /** @param {string|number} id @param {string|number} documentId @param {{ inline?: boolean }} [options] */
      downloadUrl: (id, documentId, options = {}) =>
        client.url(`/portal/document-checks/${seg(id)}/documents/${seg(documentId)}/download`, { inline: options.inline ? 1 : undefined }),
      /** Active comments of a document or a document pair (compare: auditors only). @param {string|number} id @param {{ document: string|number, compare?: string|number }} params @param {CallOptions} [opts] */
      comments: async (id, params, opts = {}) =>
        unwrap(await client.get(`/portal/document-checks/${seg(id)}/comments`, { query: compact(params), signal: opts.signal })),
      /** @param {string|number} id @param {{ document_id: number, compare_document_id?: number|null, parent_id?: number|null, body: string }} payload */
      addComment: async (id, payload) => unwrap(await client.post(`/portal/document-checks/${seg(id)}/comments`, payload)),
      /** Withdraw an own comment (hidden, not deleted) – 204. @param {string|number} id @param {string|number} commentId */
      deleteComment: (id, commentId) => client.delete(`/portal/document-checks/${seg(id)}/comments/${seg(commentId)}`),
    },

    /** Mitteilungen (F-503). The list body carries `meta.unread`; opening a detail marks it read. */
    announcements: {
      /** @param {{ page?: number, per_page?: number, property?: string|number, unread?: boolean }} [params] @param {CallOptions} [opts] */
      list: (params = {}, opts = {}) =>
        client.get('/portal/announcements', {
          query: compact({ ...params, page: params.page > 1 ? params.page : undefined, unread: params.unread ? 1 : undefined }),
          signal: opts.signal,
        }),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/announcements/${seg(id)}`, opts),
      /** → `{ unread: 0 }` */
      readAll: async () => unwrap(await client.post('/portal/announcements/read-all')),
      /** @param {string|number} id @param {string|number} documentId @param {{ inline?: boolean }} [options] */
      attachmentUrl: (id, documentId, options = {}) =>
        client.url(`/portal/announcements/${seg(id)}/attachments/${seg(documentId)}/download`, { inline: options.inline ? 1 : undefined }),
      /** F-501: `<img src>` of the image (only visible announcements); `version` = `image_version`. @param {string|number} id @param {string|number|null} [version] */
      imageUrl: (id, version) => client.url(`/portal/announcements/${seg(id)}/image`, { v: version ?? undefined }),
    },

    /**
     * Zählerablesung (F-910) – only meters whose reader is the user's party. Recording is idempotent per `client_uuid`
     * (offline queue): 409 `meter-year-closed`, `client-uuid-conflict`.
     */
    meterReadings: {
      /** → full body `{ data: [{ property: { id, name, address }, open, readings: [...] }], meta: { year, open } }` @param {{ year?: number|string }} [params] @param {CallOptions} [opts] */
      list: (params = {}, opts = {}) => client.get('/portal/meter-readings', { query: compact(params), signal: opts.signal }),
      /**
       * multipart: value, is_new_meter, client_uuid, read_at, photo, photo_2 → MeterReadingResource.
       * @param {string|number} id
       * @param {{ value: string|number, client_uuid?: string, is_new_meter?: boolean, read_at?: string|null, photo?: Blob|null, photo_2?: Blob|null }} payload
       */
      record: async (id, payload) => unwrap(await client.post(`/portal/meter-readings/${seg(id)}`, toFormData(payload))),
      /** @param {string|number} id @param {1|2} slot */
      photoUrl: (id, slot) => client.url(`/portal/meter-readings/${seg(id)}/photos/${slot === 2 ? 2 : 1}`),
    },

    /** Stimmrechtsvollmachten (F-1001, owners: `ownerships.view`) – only the user's own. */
    proxyAuthorizations: {
      /** Default: meetings from the day before yesterday on; `all` = every one. @param {{ all?: boolean }} [params] @param {CallOptions} [opts] */
      list: async (params = {}, opts = {}) =>
        unwrap(await client.get('/portal/proxy-authorizations', { query: { all: params.all ? 1 : undefined }, signal: opts.signal })),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/proxy-authorizations/${seg(id)}`, opts),
      /**
       * Creates the authorization and mails a confirmation code → full body `{ data, meta: { code_sent_to } }`.
       * `signature`: PNG data URI. 404 for properties without current ownership; 422 `invalid-signature`.
       * @param {Record<string, unknown>} payload
       */
      create: (payload) => client.post('/portal/proxy-authorizations', payload),
      /** 422 `invalid-code`, 429 `too-many-attempts` (request a new code). @param {string|number} id @param {string} code */
      confirm: async (id, code) => unwrap(await client.post(`/portal/proxy-authorizations/${seg(id)}/confirm`, { code })),
      /** → { code_sent_to }; 429 `too-many-requests`. @param {string|number} id */
      resendCode: async (id) => unwrap(await client.post(`/portal/proxy-authorizations/${seg(id)}/resend-code`)),
      /** @param {string|number} id */
      pdfUrl: (id) => client.url(`/portal/proxy-authorizations/${seg(id)}/pdf`),
    },

    /**
     * Vermietungsübersicht (F-608, G-21; `lettings.view`) – own units with current tenancy, latest target rent (Z1–Z4) in
     * net/gross basis and the letting ticket status; no tenant names. Foreign `property` → 404.
     * → `{ vat_basis, vat_rate, properties: [{ property, units: [{ unit, status: 'let'|'vacant', tenancy, rent, ticket }], totals }] }`
     */
    lettingOverview: {
      /** @param {{ vat?: 'net'|'gross', open?: boolean, property?: string|number }} [params] @param {CallOptions} [opts] */
      get: async (params = {}, opts = {}) =>
        unwrap(
          await client.get('/portal/letting-overview', {
            query: compact({ vat: params.vat, open: params.open ? 1 : undefined, property: params.property }),
            signal: opts.signal,
          }),
        ),
    },
    /** Vermietung (F-608, F-1002; `lettings.view`) – tickets of currently owned units. */
    lettingTickets: {
      /** → [{ property: { id, name }, tickets: [...], totals: { rent, garage_rent, operating_costs, heating_costs } }] @param {{ open?: boolean }} [params] @param {CallOptions} [opts] */
      list: async (params = {}, opts = {}) =>
        unwrap(await client.get('/portal/letting-tickets', { query: { open: params.open ? 1 : undefined }, signal: opts.signal })),
    },

    /** Nützliche Links (F-1003) → [{ id, name, links: [{ id, name, description, url, has_image }] }] (only categories with active links). */
    links: {
      list: (/** @type {CallOptions} */ opts) => get('/portal/links', opts),
      /** Highlighted links for the home page (F-1005, audience filtered by the API) → [{ id, name, description, url }]. */
      highlighted: (/** @type {CallOptions} */ opts) => get('/portal/links/highlighted', opts),
    },

    /**
     * Dokumente (F-405, F-807; `documents.view`): published documents in visible folders with own object relation.
     * → `[{ id, owner_type: 'property'|'unit'|'tenancy'|null, owner_id, owner_label, key, name, sort, documents: [{ id, title, original_name, mime_type, size, description, created_at, published_at, folder_id }] }]`
     * (only folders with documents).
     */
    documents: {
      /** @param {{ property?: string|number, unit?: string|number, tenancy?: string|number, scope?: 'global' }} [params] @param {CallOptions} [opts] */
      list: async (params = {}, opts = {}) => unwrap(await client.get('/portal/documents', { query: compact(params), signal: opts.signal })),
      /** @param {string|number} documentId @param {{ inline?: boolean }} [options] */
      downloadUrl: (documentId, options = {}) => client.url(`/portal/documents/${seg(documentId)}/download`, { inline: options.inline ? 1 : undefined }),
      /**
       * Volltextsuche (Titel, Dateiname, Inhalt; min. 2 Zeichen) über sichtbare Dokumente inkl. freigegebener Belegprüfungs-Ordner
       * → `[{ id, title, original_name, mime_type, size, page, snippet: [{ text, hit }]|null, locations: [{ kind: 'folder', folder_id, folder, owner_type, owner_id, label, published_at } | { kind: 'document_check', check_id, folder, label }] }]`.
       * @param {string} q @param {{ limit?: number }} [params] @param {CallOptions} [opts]
       */
      search: async (q, params = {}, opts = {}) =>
        unwrap(await client.get('/portal/documents/search', { query: compact({ q, limit: params.limit }), signal: opts.signal })),
    },

    /** Blimo AI (`assistant.use`): chat assistant over the user's portal data. */
    assistant: {
      /** → `{ enabled, permitted, configured, mcp_enabled, suggestions: string[], capabilities: [{ name, label }] }` */
      status: (/** @type {CallOptions} */ opts) => get('/portal/assistant/status', opts),
      conversations: {
        /** → `[{ id, title, last_message_at, created_at }]` (newest first, max. 100) */
        list: (/** @type {CallOptions} */ opts) => get('/portal/assistant/conversations', opts),
        /** → `{ id, title, messages: [{ role: 'user', id, text, created_at } | { role: 'assistant', id, text, tools: [{ id, name, label, status }], created_at }] }` */
        get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/assistant/conversations/${seg(id)}`, opts),
        /** 204 @param {string|number} id */
        delete: (id) => client.delete(`/portal/assistant/conversations/${seg(id)}`),
      },
      /**
       * Asks a question; the answer arrives as Server-Sent Events (`conversation`, `status`, `text`, `tool`, `notice`,
       * `error`, `ping`, `done`) via `onEvent({ event, data })`. Resolves at the end of the stream; errors before the
       * stream (503 `assistant-not-configured`, 422 `assistant-conversation-full`, 429 …) reject with ProblemError,
       * `signal.abort()` rejects with AbortError.
       * @param {{ conversation_id?: number|null, message: string }} payload
       * @param {{ onEvent: (event: import('./sse.js').SseEvent) => void, signal?: AbortSignal }} options
       */
      chat: (payload, { onEvent, signal } = /** @type {any} */ ({})) =>
        client.stream('POST', '/portal/assistant/chat', { body: compact(payload), onEvent: onEvent ?? (() => {}), signal }),
      /** Privacy notice (`status.consent`): accept the current version / revoke (deletes conversations and MCP tokens). */
      consent: {
        accept: async (/** @type {string} */ noticeVersion) => unwrap(await client.post('/portal/assistant/consent', { notice_version: noticeVersion })),
        /** → `{ deleted_conversations, revoked_tokens }` */
        revoke: async () => unwrap(await client.delete('/portal/assistant/consent')),
      },
      /** Personal MCP access tokens (only when `mcp_enabled`; 404 `assistant-mcp-disabled` otherwise). */
      mcpTokens: {
        /** → `[{ id, name, last_used_at, expires_at, created_at }]` */
        list: (/** @type {CallOptions} */ opts) => get('/portal/assistant/mcp-tokens', opts),
        /** Plaintext token is returned exactly once → `{ id, token, expires_at, endpoint }`. @param {{ name: string }} payload */
        create: async (payload) => unwrap(await client.post('/portal/assistant/mcp-tokens', payload)),
        /** 204 @param {string|number} id */
        delete: (id) => client.delete(`/portal/assistant/mcp-tokens/${seg(id)}`),
      },
    },

    /** Nachrichten an die Verwaltung (F-504) – only the user's own threads. */
    messages: {
      /** Paginated threads incl. `has_unread_reply`. @param {{ page?: number, per_page?: number }} [params] @param {CallOptions} [opts] */
      list: (params = {}, opts = {}) =>
        client.get('/portal/messages', { query: compact({ page: params.page > 1 ? params.page : undefined, per_page: params.per_page }), signal: opts.signal }),
      get: (/** @type {string|number} */ id, /** @type {CallOptions} */ opts) => get(`/portal/messages/${seg(id)}`, opts),
      /** New thread for an own unit (404 otherwise). @param {{ unit_id: number, subject: string, body: string, files?: Array<File|Blob> }} payload */
      create: async (payload) => unwrap(await client.post('/portal/messages', toFormData(payload))),
      /** @param {string|number} id @param {{ body: string, files?: Array<File|Blob> }} payload */
      followUp: async (id, payload) => unwrap(await client.post(`/portal/messages/${seg(id)}/messages`, toFormData(payload))),
      /** @param {string|number} id @param {string|number} documentId @param {{ inline?: boolean }} [options] */
      attachmentUrl: (id, documentId, options = {}) =>
        client.url(`/portal/messages/${seg(id)}/attachments/${seg(documentId)}/download`, { inline: options.inline ? 1 : undefined }),
    },
  };
}

/** @typedef {ReturnType<typeof createAdminApi>} AdminApi */
/** @typedef {ReturnType<typeof createPortalApi>} PortalApi */
/** @typedef {ReturnType<typeof createPublicApi>} PublicApi */
